import React, { useEffect, useMemo, useState } from "react";
import { FiBell, FiCheck, FiEdit3, FiMail, FiRefreshCw, FiSave, FiSliders, FiUsers, FiX } from "react-icons/fi";
import http from "../../../../api/apiClient";

const PreferencesTab = ({ t, parentData }) => {
  const parentId = parentData?.id || null;
  const [prefs, setPrefs] = useState({
    newsletter: false,
    alumni: false,
    activities: false,
    name: "",
    email: "",
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [baselinePrefs, setBaselinePrefs] = useState(null);

  const prefsKey = useMemo(() => (parentId ? `pp-alumni-parent-${parentId}` : null), [parentId]);

  useEffect(() => {
    const load = async () => {
      // Local fallback (legacy)
      if (prefsKey) {
        try {
          const stored = localStorage.getItem(prefsKey);
          if (stored) {
            const parsed = JSON.parse(stored);
            setPrefs((prev) => ({ ...prev, ...parsed }));
            setBaselinePrefs((prev) => ({ ...prev, ...parsed }));
          }
        } catch (e) {
          // ignore
        }
      }

      if (!parentId) return;
      try {
        const res = await http.get("/alumni", { params: { parentId } });
        const data = res?.data;
        if (!data) {
          setPrefs((prev) => ({
            ...prev,
            newsletter: false,
            alumni: false,
            activities: false,
          }));
          setBaselinePrefs({
            newsletter: false,
            alumni: false,
            activities: false,
            name: "",
            email: "",
          });
          return;
        }
        const parents = data?.parents || data?.parentPreferences || data;
        setPrefs((prev) => ({
          ...prev,
          newsletter: !!parents?.newsletter,
          alumni: !!parents?.alumni,
          activities: !!parents?.activities,
          name: parents?.name || prev.name || "",
          email: parents?.email || prev.email || "",
        }));
        setBaselinePrefs({
          newsletter: !!parents?.newsletter,
          alumni: !!parents?.alumni,
          activities: !!parents?.activities,
          name: parents?.name || "",
          email: parents?.email || "",
        });
      } catch (err) {
        // 204 / not found: treat as empty
        if (err?.response?.status === 204 || err?.response?.status === 404) {
          setPrefs((prev) => ({
            ...prev,
            newsletter: false,
            alumni: false,
            activities: false,
          }));
          setBaselinePrefs({
            newsletter: false,
            alumni: false,
            activities: false,
            name: "",
            email: "",
          });
        }
      }
    };

    load();
  }, [parentId, prefsKey]);

  const toggle = (key) =>
    setPrefs((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));

  const handleSave = async () => {
    if (!parentId) return;
    setSaving(true);
    setSaved(false);
    setError(null);
    const anySelected = !!(prefs.newsletter || prefs.alumni || prefs.activities);
    const payload = {
      parentId,
      childId: null,
      child: null,
      parents: {
        allowStore: anySelected,
        newsletter: !!prefs.newsletter,
        alumni: !!prefs.alumni,
        activities: !!prefs.activities,
        name:
          prefs.name ||
          [parentData?.firstName, parentData?.lastName].filter(Boolean).join(" ") ||
          null,
        email: prefs.email || parentData?.email || null,
      },
    };
    try {
      await http.post("/alumni", payload);
      if (prefsKey) {
        if (anySelected) {
          localStorage.setItem(prefsKey, JSON.stringify(prefs));
        } else {
          localStorage.removeItem(prefsKey);
        }
      }
      if (anySelected) {
        setBaselinePrefs(prefs);
      } else {
        const cleared = { newsletter: false, alumni: false, activities: false, name: "", email: "" };
        setPrefs((prev) => ({ ...prev, ...cleared }));
        setBaselinePrefs(cleared);
      }
      setSaved(true);
      setEditing(false);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          t("parentPortal.alumni.save_failed", { defaultValue: "Saving preferences failed." })
      );
    } finally {
      setSaving(false);
    }
  };

  if (!parentId) {
    return (
      <div className="mx-auto w-full max-w-5xl text-left">
        <div className="rounded-3xl border border-amber-200 bg-amber-50/80 p-5 text-sm font-semibold text-amber-900 shadow-sm dark:border-amber-900/50 dark:bg-amber-950/25 dark:text-amber-100">
          {t("parentPortal.alumni.parents_helper", {
            defaultValue: "Preferences are not available. Reload the page or contact support.",
          })}
        </div>
      </div>
    );
  }

  const yes = t("common.yes", { defaultValue: "Yes" });
  const no = t("common.no", { defaultValue: "No" });

  const prefItems = [
    {
      key: "newsletter",
      icon: <FiMail aria-hidden="true" />,
      label: t("parentPortal.alumni.opt_in_newsletter", { defaultValue: "Newsletter" }),
      hint: t("parentPortal.alumni.newsletter_hint", {
        defaultValue: "Receive the school newsletter when you are eligible.",
      }),
    },
    {
      key: "alumni",
      icon: <FiUsers aria-hidden="true" />,
      label: t("parentPortal.alumni.opt_in_alumni", { defaultValue: "Alumni updates" }),
      hint: t("parentPortal.alumni.alumni_hint", {
        defaultValue: "Receive updates related to alumni activities and news.",
      }),
    },
    {
      key: "activities",
      icon: <FiBell aria-hidden="true" />,
      label: t("parentPortal.alumni.opt_in_activities", { defaultValue: "Activities" }),
      hint: t("parentPortal.alumni.activities_hint", {
        defaultValue: "Receive messages about activities and events.",
      }),
    },
  ];

  return (
    <div className="parent-portal-preferences-tab mx-auto w-full max-w-5xl space-y-5 text-left">
      <header className="flex flex-col gap-4 rounded-3xl bg-gradient-to-br from-emerald-50 to-white p-5 ring-1 ring-emerald-100 dark:from-emerald-950/20 dark:to-slate-950/20 dark:ring-emerald-900/50 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-center gap-4">
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-emerald-600 text-white shadow-sm" aria-hidden="true">
            <FiSliders className="h-5 w-5" />
          </span>
          <span className="min-w-0">
            <h2 className="text-2xl font-black tracking-tight text-slate-950 dark:text-slate-50">
              {t("parentPortal.preferences.title", { defaultValue: "Preferences" })}
            </h2>
            <p className="mt-1 text-sm font-medium text-slate-500 dark:text-slate-400">
              {t("parentPortal.preferences.description", {
                defaultValue: "Choose which alumni and activity messages you want to receive.",
              })}
            </p>
          </span>
        </div>

        {!editing && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="inline-flex min-h-[44px] shrink-0 items-center justify-center gap-2 rounded-full bg-emerald-600 px-5 text-sm font-bold text-white shadow-sm transition hover:-translate-y-0.5 hover:bg-emerald-500 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
          >
            <FiEdit3 aria-hidden="true" />
            {t("common.actions.edit", { defaultValue: "Edit" })}
          </button>
        )}
      </header>

      <section className="rounded-3xl border border-slate-200/80 bg-white/95 p-5 shadow-sm backdrop-blur dark:border-slate-800 dark:bg-slate-950/70">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="max-w-3xl">
            <p className="text-base font-medium leading-7 text-slate-700 dark:text-slate-200">
              {t("parentPortal.alumni.parents_helper", {
                defaultValue:
                  "We only store your name and email address for the alumni channels you choose. If you turn everything off, we remove your contact preferences.",
              })}
            </p>
            <p className="mt-2 text-sm font-medium text-slate-500 dark:text-slate-400">
              {t("parentPortal.alumni.parents_stored_hint", {
                defaultValue: "Current contact: {{name}} ({{email}})",
                name:
                  [parentData?.firstName, parentData?.lastName].filter(Boolean).join(" ") ||
                  t("parentPortal.name", { defaultValue: "Name" }),
                email: parentData?.email || t("parentPortal.email", { defaultValue: "Email" }),
              })}
            </p>
          </div>

          {saved && (
            <span className="inline-flex shrink-0 items-center gap-2 rounded-full bg-emerald-50 px-3 py-1.5 text-xs font-bold text-emerald-800 ring-1 ring-emerald-100 dark:bg-emerald-950/30 dark:text-emerald-100 dark:ring-emerald-900/60">
              <FiCheck aria-hidden="true" />
              {t("common.saved", { defaultValue: "Saved" })}
            </span>
          )}
        </div>

        <div className="mt-5 grid gap-3">
          {prefItems.map(({ key, icon, label, hint }) => {
            const enabled = !!prefs[key];

            return (
              <div
                key={key}
                className={`group rounded-3xl border px-4 py-4 shadow-sm transition duration-200 hover:-translate-y-0.5 hover:shadow-md ${
                  editing
                    ? "border-slate-200 bg-white hover:border-emerald-200 dark:border-slate-800 dark:bg-slate-900/60 dark:hover:border-emerald-800"
                    : enabled
                      ? "border-emerald-100 bg-emerald-50/45 dark:border-emerald-900/50 dark:bg-emerald-950/20"
                      : "border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900/50"
                }`}
              >
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex min-w-0 items-center gap-3">
                    <span
                      className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl ring-1 transition ${
                        enabled
                          ? "bg-emerald-50 text-emerald-700 ring-emerald-100 dark:bg-emerald-950/40 dark:text-emerald-300 dark:ring-emerald-900/60"
                          : "bg-slate-50 text-slate-500 ring-slate-100 dark:bg-slate-950/50 dark:text-slate-400 dark:ring-slate-800"
                      }`}
                    >
                      {icon}
                    </span>
                    <span className="min-w-0">
                      <span className="block text-base font-black text-slate-950 dark:text-white">
                        {label}
                      </span>
                      <span className="mt-1 block text-sm font-medium leading-5 text-slate-500 dark:text-slate-400">
                        {hint}
                      </span>
                    </span>
                  </div>

                  {editing ? (
                    <button
                      type="button"
                      role="switch"
                      aria-checked={enabled}
                      onClick={() => toggle(key)}
                      className={`relative flex h-8 w-14 shrink-0 items-center rounded-full transition focus:outline-none focus:ring-4 focus:ring-emerald-100 dark:focus:ring-emerald-900/30 ${
                        enabled ? "bg-emerald-600" : "bg-slate-300 dark:bg-slate-700"
                      }`}
                    >
                      <span
                        className={`absolute left-1 h-6 w-6 rounded-full bg-white shadow-sm transition-transform ${
                          enabled ? "translate-x-6" : ""
                        }`}
                      />
                    </button>
                  ) : (
                    <span
                      className={`inline-flex shrink-0 rounded-full px-3 py-1 text-xs font-black ${
                        enabled
                          ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-100"
                          : "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-300"
                      }`}
                    >
                      {enabled ? yes : no}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {editing && (
          <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50/70 px-4 py-3 text-sm font-medium text-slate-500 dark:border-slate-800 dark:bg-slate-900/40 dark:text-slate-400">
            {t("parentPortal.alumni.disable_all_hint", {
              defaultValue: "If you choose not to receive anything, your contact preferences will be removed.",
            })}
          </div>
        )}
      </section>

      {editing && (
        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={() => {
              if (baselinePrefs) setPrefs(baselinePrefs);
              setEditing(false);
            }}
            className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white/85 px-5 text-sm font-bold text-slate-700 shadow-sm transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950/30 dark:text-slate-200"
          >
            <FiX aria-hidden="true" />
            {t("common.cancel", { defaultValue: "Cancel" })}
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-5 text-sm font-bold text-white shadow-sm transition hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {saving ? <FiRefreshCw className="animate-spin" aria-hidden="true" /> : <FiSave aria-hidden="true" />}
            {saving ? t("common.saving", { defaultValue: "Saving..." }) : t("common.save", { defaultValue: "Save" })}
          </button>
        </div>
      )}

      {error && (
        <span className="block rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700 dark:border-rose-900/60 dark:bg-rose-950/25 dark:text-rose-100">
          {error}
        </span>
      )}

      <style>{`
        @keyframes lumenPortalScreenSlideIn {
          from { opacity: 0; transform: translateX(32px); }
          to { opacity: 1; transform: translateX(0); }
        }
        .parent-portal-preferences-tab {
          animation: lumenPortalScreenSlideIn 420ms cubic-bezier(0.22, 1, 0.36, 1) both;
        }
        @media (prefers-reduced-motion: reduce) {
          .parent-portal-preferences-tab { animation: none; }
        }
      `}</style>
    </div>
  );
};

export default PreferencesTab;